import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { ResponsivePopup } from 'src/shared/components/ui/popups/responsive-popup'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { usePopupStore } from 'src/shared/store/popup-store'

import { SendReferralContent } from './send-referral-content'

interface SendReferralDialogProps {
  trigger: React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>
  receiverUsername: string
  onBeforeOpen?: () => void
}

const SendReferralDialog: React.FC<SendReferralDialogProps> = ({ trigger, receiverUsername, onBeforeOpen }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSendReferralOpen, setIsSendReferralOpen] = useState(false)
  const { data: user } = useMyUserData()
  const { setShowCompleteProfile } = usePopupStore()
  const isMobile = useIsMobile()

  const currentSendReferralStep = useSendReferralStore((state) => state.currentSendReferralStep)
  const getHeader = useSendReferralStore((state) => state.getHeader)
  const resetSendReferralStore = useSendReferralStore((state) => state.resetSendReferralStore)

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetSendReferralStore()
      setIsSendReferralOpen(false)
      return
    }
    setIsSendReferralOpen(true)
  }

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const profileComplete = user?.profileComplete ?? 0
    if (profileComplete < 100) {
      if (isMobile) {
        navigate(paths.users.completeProfile.getHref(location.pathname))
      } else {
        setShowCompleteProfile(true)
      }
      return
    }

    onBeforeOpen?.()

    if (isMobile) {
      navigate(paths.referrals.send.getHref(receiverUsername))
    } else {
      setIsSendReferralOpen(true)
    }
  }

  const wrappedTrigger = React.cloneElement(trigger, {
    onClick: (e: React.MouseEvent) => handleTriggerClick(e),
  })

  return (
    <ResponsivePopup
      open={isSendReferralOpen}
      onOpenChange={handleOpenChange}
      trigger={wrappedTrigger}
      title={getHeader().title}
      mobileRoute={paths.referrals.send.getHref(receiverUsername)}
      description={getHeader().description}
      hideHeader={currentSendReferralStep === 5}
      dark={currentSendReferralStep === 5}
    >
      <SendReferralContent />
    </ResponsivePopup>
  )
}

export default SendReferralDialog
